import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  OnChanges,
  SimpleChanges,
  HostListener,
  ElementRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GuidUtil } from '@liberty-financial/ng-core';

import { UserList } from '@core/services/user';
import { UserService } from '@core/services/user.service';
import { DropdownItem } from '@shared/components/dropdown/dropdown.model';

@Component({
  selector: 'app-user-dropdown',
  templateUrl: './user-dropdown.component.html',
  styleUrls: ['./user-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserDropdownComponent),
      multi: true
    }
  ]
})
export class UserDropdownComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  @Input() users: UserList = new UserList([]);
  @Input() selectedUserId: string | null = null;
  @Input() placeholder: string = 'Select user...';
  @Input() includeAllOption: boolean = false;
  @Input() label: string = '';
  @Input() identifier: string = GuidUtil.generate();
  @Input() disabled: boolean = false;

  @Output() userSelected = new EventEmitter<string>();

  public userDropdownItems: DropdownItem<string>[] = [];
  public isDropdownOpen = false;

  // ControlValueAccessor implementation
  private onChange = (value: string | null) => {};
  private onTouched = () => {};

  constructor(
    private readonly userService: UserService,
    private readonly elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.transformUsersToDropdownItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-transform users when the users input changes
    if (changes['users'] && !changes['users'].firstChange) {
      this.transformUsersToDropdownItems();
    }
  }

  // ControlValueAccessor methods
  writeValue(value: string | null): void {
    this.selectedUserId = value;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private transformUsersToDropdownItems(): void {
    const items: DropdownItem<string>[] = [];

    // Add "All" option if requested
    if (this.includeAllOption) {
      items.push({
        id: ' ',
        label: 'All',
        description: '',
        value: ' '
      });
    }

    // Add current user first if available
    const currentUser = this.userService.getCurrentUser(this.users);
    if (currentUser) {
      items.push({
        id: currentUser.portfolioId || '',
        label: `${currentUser.firstName} ${currentUser.lastName}` || '',
        description: currentUser.id || '',
        value: currentUser.portfolioId || ''
      });
    }

    // Sort other users alphabetically and add them
    const otherUsers = this.userService.getOtherUsers(this.users);
    const sortedUsers = [...otherUsers].sort((a, b) => {
      const nameA = (a.displayName || '').toLowerCase();
      const nameB = (b.displayName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    const otherUserItems = sortedUsers.map((user) => ({
      id: user.portfolioId,
      label: user.displayName,
      description: user.id,
      value: user.portfolioId
    }));

    this.userDropdownItems = [...items, ...otherUserItems];
  }

  public toggleDropdown(event?: Event): void {
    if (this.disabled) {
      return;
    }

    // Prevent event bubbling to avoid conflicts
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.onTouched();
    }
  }

  public closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  public onUserSelectedInternal(portfolioId: string): void {
    if (this.disabled) {
      return;
    }

    this.selectedUserId = portfolioId;
    this.onChange(portfolioId);
    this.userSelected.emit(portfolioId);
    this.closeDropdown();
  }

  public getSelectedUserDisplay(): string {
    if (!this.selectedUserId || this.selectedUserId === ' ') {
      return this.includeAllOption ? 'All' : this.placeholder;
    }

    const selectedUser = this.userDropdownItems.find(
      (item) => item.value === this.selectedUserId
    );
    return selectedUser ? selectedUser.label : this.placeholder;
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  // Handle keyboard navigation
  @HostListener('keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    event.preventDefault();
    this.closeDropdown();
  }

  @HostListener('keydown.enter', ['$event'])
  @HostListener('keydown.space', ['$event'])
  onActivationKey(event: KeyboardEvent): void {
    if (!this.isDropdownOpen) {
      event.preventDefault();
      this.toggleDropdown();
    }
  }
}
